import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Globe2, Loader2, Sparkles, Play, RefreshCw, Sun, Sunrise, Sunset, Moon, Heart, CalendarClock, MessageCircle, Volume2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Bits";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SLOTS = [
  { key: "morning", label: "Morning", icon: Sunrise },
  { key: "day", label: "Midday", icon: Sun },
  { key: "evening", label: "Evening", icon: Sunset },
  { key: "night", label: "Night", icon: Moon },
];
const TIERS = [
  { key: "stranger", label: "Stranger", cls: "border-slate-500/40 bg-slate-500/10 text-slate-400" },
  { key: "friend", label: "Friend", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-500" },
  { key: "ally", label: "Ally", cls: "border-violet-500/40 bg-violet-500/10 text-violet-500" },
];

export default function LivingWorld() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [world, setWorld] = useState(null);
  const [hint, setHint] = useState("");
  const [generating, setGenerating] = useState(false);
  const [slot, setSlot] = useState("morning");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/living-world/${gameId}`);
      setWorld(data?.exists ? data : null);
    } catch (e) { toast.error("Couldn't open the living world."); }
    finally { setLoading(false); }
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/living-world/${gameId}/generate`, { hint: hint.trim() }, { timeout: 120000 });
      setWorld(data);
      toast.success("🌍 Your world is alive! NPCs now have routines, personalities and memory.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" ? d : formatApiError(d)) || "Couldn't bring the world to life.");
    } finally { setGenerating(false); }
  };

  const npcs = world?.npcs || [];
  const events = world?.world_events || [];

  const playVoice = (npcId, tier) => {
    try {
      if (window.__lwStudioAudio) window.__lwStudioAudio.pause();
      const a = new Audio(`${BACKEND}/api/living-world/${gameId}/voice/${npcId}/${tier}.mp3`);
      window.__lwStudioAudio = a;
      a.play().catch(() => toast.error("Couldn't play the voice — try again."));
    } catch { /* ignore */ }
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="living-world">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/app/build/${gameId}`)} data-testid="lw-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-bold flex items-center gap-2 truncate"><Globe2 className="w-6 h-6 text-emerald-500" /> Living World</h1>
            <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">Daily routines · memory of the player · a world that quietly changes between sessions</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {world && <Button variant="outline" size="sm" className="rounded-full" onClick={generate} disabled={generating} data-testid="lw-regenerate-btn">{generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Regenerate</Button>}
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => navigate(`/app/arcade/${gameId}`)} data-testid="lw-play-btn"><Play className="w-4 h-4" /> Play</Button>
        </div>
      </div>

      {!world ? (
        <div className="rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-sky-500/5 p-8 text-center" data-testid="lw-empty">
          <Globe2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
          <h2 className="font-display text-xl font-bold mb-1.5">Bring your world to life</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-md mx-auto mb-4">Your AI cast will get daily routines (home → work → tavern), distinct personalities, and greetings that warm up as players earn their trust. Players will get a "While you were away…" recap each time they return.</p>
          <textarea
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            placeholder="Optional: any vibe for the town? e.g. 'a cozy seaside village where everyone knows each other'"
            maxLength={400}
            rows={2}
            data-testid="lw-hint-input"
            className="w-full max-w-md mx-auto block rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm resize-none mb-4"
          />
          <Button className="rounded-full" onClick={generate} disabled={generating} data-testid="lw-generate-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {generating ? "Waking the world…" : "Bring the world to life"}
          </Button>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">Uses 3 AI credits</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Day scrubber */}
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] flex items-center gap-1.5"><CalendarClock className="w-4 h-4" /> Simulate a day — see where everyone is</p>
            </div>
            <div className="flex items-center gap-2" data-testid="lw-day-scrubber">
              {SLOTS.map((s) => {
                const on = slot === s.key;
                const Icon = s.icon;
                return (
                  <button key={s.key} onClick={() => setSlot(s.key)} data-testid={`lw-slot-${s.key}`}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2.5 text-xs font-semibold transition-colors ${on ? "border-emerald-500 bg-emerald-500/10 text-emerald-500" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>
                    <Icon className="w-4 h-4" /> {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* NPC roster */}
          <div>
            <h2 className="font-display text-lg font-bold mb-3">The living cast ({npcs.length})</h2>
            <div className="grid md:grid-cols-2 gap-4" data-testid="lw-npc-grid">
              {npcs.map((n) => (
                <div key={n.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid={`lw-npc-${n.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-full bg-emerald-500/15 grid place-items-center text-lg shrink-0">🧑‍🌾</div>
                    <div className="min-w-0 flex-1">
                      <p className="font-display font-bold truncate">{n.name}</p>
                      <p className="text-[11px] uppercase tracking-wide text-emerald-500 font-semibold">{n.role}</p>
                    </div>
                  </div>
                  {n.personality && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 italic">"{n.personality}"</p>}

                  {/* current activity */}
                  <div className="mt-3 rounded-xl bg-[hsl(var(--secondary))] px-3 py-2" data-testid={`lw-npc-activity-${n.id}`}>
                    <p className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{SLOTS.find((s) => s.key === slot)?.label}</p>
                    <p className="text-sm font-medium">{(n.schedule || {})[slot] || "…"}</p>
                  </div>

                  {/* relationship greetings */}
                  <div className="mt-3 space-y-1.5">
                    <p className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] flex items-center gap-1"><Heart className="w-3 h-3" /> How they greet you as you earn trust · tap 🔊 to hear</p>
                    {TIERS.map((t) => (
                      <div key={t.key} className="flex items-start gap-2">
                        <span className={`shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold ${t.cls}`}>{t.label}</span>
                        <span className="text-xs text-[hsl(var(--muted-foreground))] flex-1">"{(n.greetings || {})[t.key]}"</span>
                        <button onClick={() => playVoice(n.id, t.key)} title={`Hear ${n.name} (${t.label})`} data-testid={`lw-voice-${n.id}-${t.key}`} className="shrink-0 text-[hsl(var(--muted-foreground))] hover:text-emerald-500 transition-colors mt-0.5"><Volume2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* World events */}
          {events.length > 0 && (
            <div>
              <h2 className="font-display text-lg font-bold mb-1">Between your visits…</h2>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">These quietly unfold across sessions and appear in each player's "While you were away…" recap.</p>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="lw-events">
                {events.map((e, i) => (
                  <div key={i} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3" data-testid={`lw-event-${i}`}>
                    <p className="text-sm font-bold flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-amber-500" /> {e.title}</p>
                    {e.detail && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{e.detail}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-4 flex items-start gap-3">
            <MessageCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
            <p className="text-sm text-[hsl(var(--muted-foreground))]">In-game, these NPCs greet returning players by name, deepen from <b>stranger → friend → ally</b> as players talk, gift and quest with them, and shift activities by time of day. Hit <b>Play</b> to see your world come alive.</p>
          </div>
        </div>
      )}
    </div>
  );
}
